import styles from "./PolicyInfo.module.scss";
import { ListCard, Tooltip } from "@hagerty/self-service-components";
import { ReactComponent as TooltipIcon } from "../../../svg/tooltip.svg";
import { formatPolicyNumber } from "../../../utils/formatPolicyNumber";
import { DateRange } from "../../DateRange";
import { Currency } from "../../Currency";
import { Address } from "../../Address";
import { useUrls } from "../../../app/Providers/AppContext";
import { Link } from "../../Link";
import { PolicyInfoBroker } from "./PolicyInfoBroker";
import clsx from "clsx";
import { useState } from "react";
import { phoneNumbers, PolicyDetails } from "shared";
import { TagAction } from "../../../types/Tag.types";

type PolicyInfoProps = {
  policy: PolicyDetails;
};

const managePaperlessTagAction: TagAction = {
  actionName: "Manage paperless",
  actionType: "link",
};

export const PolicyInfo = ({ policy }: PolicyInfoProps) => {
  const [redirectEditAddress, setRedirectEditAddress] = useState(false);
  const [redirectManagePaperless, setRedirectManagePaperless] = useState(false);

  const urls = useUrls();
  const {
    policyNumber,
    effectiveDate,
    expirationDate,
    premium,
    country,
    policyAddress,
    isAutoPay,
  } = policy;

  return (
    <ListCard className={styles.wrapper}>
      <ListCard.Row>
        <div className={styles.column}>
          <div className={styles.name}>Policy number</div>
          <div className={styles.details}>
            #{formatPolicyNumber(policyNumber)}
          </div>
        </div>
      </ListCard.Row>
      <ListCard.Row>
        <div className={styles.column}>
          <div className={styles.name}>Effective from</div>
          <div className={styles.details}>
            <DateRange start={effectiveDate} end={expirationDate} />
          </div>
        </div>
      </ListCard.Row>
      {premium && (
        <ListCard.Row>
          <div className={styles.column}>
            <div className={styles.name}>
              Annual Premium
              {isAutoPay && (
                <span className={styles.editAutoPay}>
                  To edit Auto Pay, call{" "}
                  <Link phoneNumber={phoneNumbers.auto} />
                </span>
              )}
            </div>
            <div className={styles.details}>
              <div>
                {isAutoPay && <span className={styles.autoPay}>Auto Pay</span>}
                <Currency
                  amount={premium}
                  decimalPlaces={2}
                  ariaLabel={`Policy ${policyNumber} annual premium ${premium} value`}
                  country={country}
                />{" "}
                / year
              </div>
            </div>
          </div>
        </ListCard.Row>
      )}
      {!!policyAddress && (
        <ListCard.Row>
          <div className={styles.column}>
            <div className={styles.name}>
              Policy address
              <Tooltip
                content="This is the address where your vehicles are registered."
                id="policyAddressTooltip"
                className={styles.tooltip}
                placement="top"
              >
                <TooltipIcon className={styles.tooltipIcon} />
              </Tooltip>
            </div>
            <div className={styles.details}>
              <Address
                line1={policyAddress.lineOne}
                line2={policyAddress.lineTwo ?? ""}
                city={policyAddress.city}
                state={policyAddress.stateRegion}
                postalCode={policyAddress.postalCode}
              />
              <Link
                className={clsx(styles.link, styles.action)}
                href={urls.account.changeAddress}
                isLoading={redirectEditAddress}
                shrinkToSpinner
                onClick={() => setRedirectEditAddress(true)}
              >
                Edit
              </Link>
            </div>
          </div>
        </ListCard.Row>
      )}
      <PolicyInfoBroker policy={policy} />
      <ListCard.Row>
        <div className={styles.column}>
          <div className={styles.name}>Document delivery</div>
          <div className={styles.details}>
            <Link
              className={clsx(styles.link, styles.action)}
              tagAction={managePaperlessTagAction}
              href={urls.insurance.changeDocumentDelivery(country)}
              isLoading={redirectManagePaperless}
              shrinkToSpinner
              onClick={() => setRedirectManagePaperless(true)}
            >
              Manage paperless
            </Link>
          </div>
        </div>
      </ListCard.Row>
    </ListCard>
  );
};
